function normalizeMonthDayRecord(day) {
  if (!day || typeof day !== "object") return null;
  const date = String(day.date || day.serviceDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  return {
    ...day,
    date,
    status: String(day.status || "NOT_AVAILABLE").toUpperCase(),
    isPast: Boolean(day.isPast),
    bookedCount: Number(day.bookedCount) || 0,
    workingHoursCount: Number(day.workingHoursCount) || 0,
    leaveCount: Number(day.leaveCount) || 0,
  };
}

/** Unwrap GET /caregiver/availability/month (and ops equivalent) day list. */
export function parseAvailabilityMonthDays(res) {
  const raw = Array.isArray(res?.days)
    ? res.days
    : Array.isArray(res?.data?.days)
      ? res.data.days
      : Array.isArray(res?.payload?.days)
        ? res.payload.days
        : [];
  return raw.map(normalizeMonthDayRecord).filter(Boolean);
}

export function parseHasCustomerBookings(res) {
  if (res?.hasCustomerBookings != null) return Boolean(res.hasCustomerBookings);
  if (res?.data?.hasCustomerBookings != null) return Boolean(res.data.hasCustomerBookings);
  return null;
}

/** When the API omits hasCustomerBookings (older BE), infer from month payload. */
export function inferHasCustomerBookings(apiDays, explicit) {
  if (explicit != null) return explicit;
  const days = Array.isArray(apiDays) ? apiDays : [];
  if (!days.length) return null;
  if (days.some((d) => Number(d.bookedCount) > 0)) return true;
  const future = days.filter((d) => !d.isPast);
  if (
    future.length > 0 &&
    future.every(
      (d) =>
        ["NOT_AVAILABLE", "BLOCKED"].includes(String(d.status || "").toUpperCase()) &&
        Number(d.workingHoursCount) === 0 &&
        Number(d.bookedCount) === 0 &&
        Number(d.leaveCount) === 0
    )
  ) {
    return false;
  }
  return null;
}

/** Fill missing month cells; default open days for new caregivers when API omits roster fallback. */
export function mergeAvailabilityMonthGrid({ year, month, apiDays, todayIso, hasCustomerBookings }) {
  const pad2 = (n) => String(Math.max(0, Number(n) || 0)).padStart(2, "0");
  const byDate = new Map();
  for (const day of apiDays || []) {
    byDate.set(day.date, day);
  }
  const first = new Date(`${year}-${pad2(month)}-01T12:00:00+05:30`);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const merged = [];
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    const iso = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const existing = byDate.get(iso);
    const isPast = Boolean(todayIso && iso < todayIso);
    if (existing) {
      const st = String(existing.status || "").toUpperCase();
      const openOverride =
        hasCustomerBookings === false &&
        !isPast &&
        st === "NOT_AVAILABLE" &&
        Number(existing.bookedCount) === 0 &&
        Number(existing.leaveCount) === 0;
      merged.push(
        openOverride
          ? {
              ...existing,
              status: "AVAILABLE",
              workingHoursCount: Math.max(1, Number(existing.workingHoursCount) || 1),
              isPast,
            }
          : { ...existing, isPast: existing.isPast || isPast }
      );
      continue;
    }
    const openByDefault = hasCustomerBookings === false && !isPast;
    merged.push({
      date: iso,
      dayOfWeek: d.getDay(),
      status: openByDefault ? "AVAILABLE" : isPast ? "PAST" : "NOT_AVAILABLE",
      isPast,
      bookedCount: 0,
      workingHoursCount: openByDefault ? 1 : 0,
      leaveCount: 0,
    });
  }
  return merged;
}

/**
 * Parse unavailable caregiver IDs from GET /marketplace/unavailable-on-date.
 */
export function parseUnavailableCaregiverIds(res) {
  const list = Array.isArray(res?.unavailableCaregiverIds)
    ? res.unavailableCaregiverIds
    : Array.isArray(res?.data?.unavailableCaregiverIds)
      ? res.data.unavailableCaregiverIds
      : [];
  return new Set(list.map((id) => String(id)));
}

/**
 * Returns true when the caregiver is unavailable for the given service date.
 * @param {{ leaveOnly?: boolean }} [options] - when leaveOnly, only all-day leave/block counts
 *   (used by Instant/Recurring booking so roster/duty don't hard-stop slot generation).
 */
export async function isCaregiverUnavailableOnDate(
  api,
  { caregiverId, serviceDate, leaveOnly = false }
) {
  const id = String(caregiverId || "").trim();
  const date = String(serviceDate || "").slice(0, 10);
  if (!id || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const res = await api.getCaregiversUnavailableOnDate({
    serviceDate: date,
    caregiverIds: [id],
    leaveOnly: Boolean(leaveOnly),
  });
  return parseUnavailableCaregiverIds(res).has(id);
}

/**
 * Throws if the caregiver is unavailable on any of the given dates.
 * @param {{ leaveOnly?: boolean }} [options]
 */
export async function assertCaregiverAvailableOnDates(
  api,
  { caregiverId, serviceDates, leaveOnly = false }
) {
  const id = String(caregiverId || "").trim();
  const dates = [
    ...new Set(
      (Array.isArray(serviceDates) ? serviceDates : [serviceDates])
        .map((d) => String(d || "").slice(0, 10))
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    ),
  ];
  if (!id || !dates.length) return;

  for (const date of dates) {
    const unavailable = await isCaregiverUnavailableOnDate(api, {
      caregiverId: id,
      serviceDate: date,
      leaveOnly,
    });
    if (unavailable) {
      const err = new Error(
        leaveOnly
          ? `This caregiver is unavailable on ${date} (blocked / leave day). Please choose another date or caregiver.`
          : `This caregiver is unavailable on ${date} (blocked / leave day or not rostered). Please choose another date or caregiver.`
      );
      err.code = "CAREGIVER_BLOCKED_ON_DATE";
      throw err;
    }
  }
}

/**
 * Fetch per-date availability for the booking calendar.
 * On failure returns `{ ok: false }` — callers must not treat all dates as available.
 */
export async function fetchBookingCalendarAvailability(
  api,
  {
    caregiverId,
    fromDate,
    toDate,
    days,
    duration,
    serviceRole,
    serviceId,
    lat,
    lng,
    instantOnly = false,
    excludeBookingId,
  }
) {
  const id = String(caregiverId || "").trim();
  const start = String(fromDate || "").slice(0, 10);
  if (!id || !/^\d{4}-\d{2}-\d{2}$/.test(start)) {
    return { ok: false, error: "Missing caregiver or date range", daysByDate: {}, availableDates: [] };
  }
  try {
    const res = await api.getAvailabilityCalendar({
      caregiverId: id,
      fromDate: start,
      toDate,
      days,
      duration,
      serviceRole,
      serviceId,
      lat,
      lng,
      instantOnly,
      excludeBookingId,
    });
    const daysList = Array.isArray(res?.days)
      ? res.days
      : Array.isArray(res?.data?.days)
        ? res.data.days
        : [];
    const daysByDate = {};
    for (const day of daysList) {
      const iso = String(day?.date || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
      daysByDate[iso] = {
        available: Boolean(day.available),
        reason: day.reason || null,
        message: day.message || null,
        onLeave: Boolean(day.onLeave),
        slotCount: Number(day.slotCount) || 0,
      };
    }
    const availableDates = Object.keys(daysByDate).filter((d) => daysByDate[d].available);
    return {
      ok: true,
      daysByDate,
      availableDates,
      unavailableDates: Object.keys(daysByDate).filter((d) => !daysByDate[d].available),
      fromDate: res?.fromDate || start,
      toDate: res?.toDate || toDate || null,
      bookingWindowDays: res?.bookingWindowDays ?? null,
      today: res?.today || null,
    };
  } catch (err) {
    return {
      ok: false,
      error: err?.message || "Unable to load caregiver availability",
      daysByDate: {},
      availableDates: [],
    };
  }
}

/** True when a date is bookable given calendar map + window bounds. */
export function isCalendarDateSelectable(iso, { daysByDate, minDate, maxDate, availabilityLoaded }) {
  const date = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  if (minDate && date < minDate) return false;
  if (maxDate && date > maxDate) return false;
  if (!availabilityLoaded) return false;
  const entry = daysByDate?.[date];
  if (!entry) return false;
  return Boolean(entry.available);
}
